import React from 'react';
import { Drawer, Heading } from '@chakra-ui/react';
import { CloseButton } from '@/components/ui/close-button';
import LeftPanel from '@/components/common/layout/LeftPanel';
import { useAssistantSessions } from '@/hooks/assistant/useAssistantSessions';
import { ExchangeOut } from '@/client';
import SessionList from './chat-history/SessionList';

interface ChatHistoryDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	houseId: number;
	containerRef?: React.RefObject<HTMLElement>;
	onResumeSession: (sessionId: string, exchanges: ExchangeOut[]) => void;
	currentSessionId: string | null;
	currentSessionTitle: string;
	currentSessionCreatedAt: string | null;
	currentSessionIsStreaming: boolean;
}

/**
 * Drawer that lists all past assistant sessions for the current house.
 * Mirrors the NotificationsDrawer pattern using LeftPanel.
 */
const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
	isOpen,
	onClose,
	houseId,
	containerRef,
	onResumeSession,
	currentSessionId,
	currentSessionTitle,
	currentSessionCreatedAt,
	currentSessionIsStreaming,
}) => {
	const { data: sessions, isLoading, isError, refetch } = useAssistantSessions(houseId, isOpen);

	const handleResume = (sessionId: string, exchanges: ExchangeOut[]) => {
		onResumeSession(sessionId, exchanges);
		onClose();
	};

	return (
		<LeftPanel
			isOpen={isOpen}
			onOpenChange={(e) => !e.open && onClose()}
			width="420px"
			showBackdrop={false}
			containerRef={containerRef}
		>
			<Drawer.Header borderBottom="1px solid" borderColor="border.default" pb={4}>
				<Heading size="lg" color="brand.fg">
					Chat history
				</Heading>
				<Drawer.CloseTrigger asChild position="absolute" top={4} right={4}>
					<CloseButton size="sm" />
				</Drawer.CloseTrigger>
			</Drawer.Header>

			<Drawer.Body p={0} h="100%" overflowY="auto" overflowX="hidden">
				<SessionList
					sessions={sessions}
					isLoading={isLoading}
					isError={isError}
					onRetry={() => void refetch()}
					houseId={houseId}
					onResume={handleResume}
					onSelectCurrent={onClose}
					currentSessionId={currentSessionId}
					currentSessionTitle={currentSessionTitle}
					currentSessionCreatedAt={currentSessionCreatedAt}
					currentSessionIsStreaming={currentSessionIsStreaming}
				/>
			</Drawer.Body>
		</LeftPanel>
	);
};

export default ChatHistoryDrawer;
